import { Dispatch, useCallback, useEffect, useMemo } from "react";

import { Channel } from "pusher-js";
import { UserAction } from "store/actions";

import { useSocket } from "../../components";
import {
  IAction,
  ISocketService,
  useAccountSelector,
  useDealActions,
  useDispatch,
  useUserActions,
  useUserIdSelector
} from "../../store";
import { INotification } from "../../store/types";

const eventsList: string[] = [
  "notifications.form.shared",
  "notifications.form.new",
  "notifications.form.deleted",
  "notifications.form.edited",
  "notifications.form.finished",
  "notifications.folder.shared",
  "notifications.folder.new",
  "notifications.folder.deleted",
  "notifications.folder.edited",
  "notifications.comment.new",
  "notifications.comment.edited",
  "notifications.comment.deleted",
  "notifications.reply.new",
  "notifications.answer.published",
  "notifications.file.shared",
  "notifications.file.new",
  "notifications.file.deleted",
  "notifications.file.new_in_question",
  "notifications.file.new_in_form",
  "notifications.import.started",
  "notifications.import.failed",
  "notifications.import.finished",
  "notifications.crunchbase.submitted",
  "notifications.crunchbase.failed",
  "notifications.note.edited",
  "notifications.note.new",
  "notifications.note.deleted",
  "notifications.pdf_tagger.finished",
  "notifications.pdf_tagger.tagger_link",
  // Datarooms
  "notifications.dataroom.started",
  "notifications.dataroom.downloadFile",
  "notifications.dataroom.finished",
  "notifications.dataroom.download",
  "notifications.dataroom.synced",
  "notifications.dataroom.failed",
  //Form AI
  "notifications.answer_questions.finished",
  "notifications.answer_questions.failed",
  "notifications.parse_pdf_file.failed",
  //Characteristics
  "notifications.characteristic.new",
  "notifications.characteristic.edited",
  "notifications.folder.uploaded",
  //Documents
  "notifications.document_answer_questions.finished",
  //Default
  "notifications.default.notification",
  //Ai Extraction files upload
  "notifications.ai_extraction.finished",
  "notifications.ai_extraction.failed",
  "notifications.classification.started",
  "notifications.classification.finished",
  "notifications.classification.failed"
];

export function useNotificationsSocketHook(): void {
  const dispatch: Dispatch<IAction> = useDispatch();
  const socket: ISocketService = useSocket();
  const userId: number | undefined = useUserIdSelector();
  const { id: accountId } = useAccountSelector() || {};

  const getCurrentDealId = () => {
    const dealIdMatch = window.location.pathname.match(/\/deal\/(\d+)/);
    return dealIdMatch ? parseInt(dealIdMatch[1], 10) : null;
  };

  const { getFilesAction, getFoldersAction, getFormsAction, getNotesAction, getOpportunitiesAction } = useDealActions();
  const { getNotificationsAction } = useUserActions();

  let notificationsChannel: Channel | null = null;

  const channelName: string = useMemo(() => `private-notifications.${userId}.${accountId}`, [userId, accountId]);

  useEffect(() => {
    if (userId) {
      notificationsChannel = socket.subscribe(channelName);
    }
  }, [userId, socket]);

  // Unsubscribe onWillOnMount
  useEffect(() => {
    return () => socket.unsubscribe(channelName);
  }, [channelName]);

  const notificationDispatcher = useCallback(
    ({ notification }: { notification: INotification }) => {
      dispatch({
        type: UserAction.CreateNotificationSocket,
        payload: notification
      });
      console.log("[Notification arrived] : ", notification);
      const currDealId = getCurrentDealId();
      if (
        notification.components.deal_id == currDealId &&
        (notification.action === "file" ||
          notification.action === "folder" ||
          notification.action === "note" ||
          notification.action === "form" ||
          notification.action === "answer_questions" ||
          notification.action === "ai_extraction" ||
          notification.action === "classification")
      ) {
        handleNotificationAction(notification);
      }
    },
    [dispatch]
  );

  const handleNotificationAction = async (notification: INotification) => {
    const dealId = notification.components.deal_id;
    switch (notification.action) {
      case "file": {
        const fileId = notification.components.file_id;
        await getFilesAction(dealId);
        console.log(notification.components.type, " file ", fileId);
        break;
      }
      case "folder": {
        const folderId = notification.components.section_id;
        if (userId !== notification.sender_id) {
          await getFoldersAction({ dealId });
        }
        console.log(notification.components.type, " folder ", folderId);
        break;
      }
      case "note": {
        const noteId = notification.components.note_id;
        await getNotesAction(dealId);
        console.log(notification.components.type, " note ", noteId);
        break;
      }
      case "form": {
        const formId = notification.components.form_id;
        await getFormsAction(dealId);
        console.log(notification.components.type, " form ", formId);
        break;
      }
      case "answer_questions": {
        const formId = notification.components.form_id;
        await getFormsAction(dealId);
        console.log(notification.components.type, " form ", formId);
        break;
      }
      case "parse_pdf_file": {
        const formId = notification.components.form_id;
        await getFormsAction(dealId);
        console.log(notification.components.type, " form ", formId);
        break;
      }
      case "ai_extraction": {
        await getFoldersAction({ dealId });
        await getFilesAction(dealId);
        console.log(notification.components.type, " ai_extraction ");
        break;
      }
      case "classification": {
        if (notification.components.type !== "started") {
          await getFoldersAction({ dealId });
          await getFilesAction(dealId);
          console.log(notification.components.type, " classification ", dealId);
        }
        break;
      }
    }
  };

  // Listen events
  useEffect(() => {
    if (notificationsChannel) {
      // Listen success connection to notifications;
      notificationsChannel.bind("pusher:subscription_succeeded", async () => {
        await getNotificationsAction(); // get notifications
        console.log("[private-notifications]: connection succeed");
      });

      // Listen error connection
      notificationsChannel.bind("pusher:subscription_error", (error: any) => {
        console.log("[private-notifications]: connection error", error);
      });

      notificationsChannel.bind("error", (error: any) => {
        console.log("[notifications error]: ", error);
      });

      // ---------------------- Notifications unread items -----------------------

      notificationsChannel.bind("notifications.unread", ({ unreadNotifications }: { unreadNotifications: number }) => {
        dispatch({
          type: UserAction.ReceiveUnreadNotifications,
          payload: unreadNotifications
        });
      });

      eventsList.forEach((eventName: string) => {
        notificationsChannel?.bind(eventName, notificationDispatcher);
      });

      notificationsChannel?.bind("opportunity", () => {
        getOpportunitiesAction();
      });
    }
  }, [notificationsChannel, dispatch]);
}
